import { useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { Building2, GraduationCap, Link2, Upload } from 'lucide-react';
import {
  useGetPartnerByIdQuery,
  useVerifyPartnerMutation,
  useSuspendPartnerMutation,
  useReinstatePartnerMutation,
  useApprovePartnerNameMutation,
  useRejectPartnerNameMutation,
} from '../../reactWrapper/Redux/apiSlice';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { StatTile } from '../../components/ui-x';
import Status from '../../components/Status/Status';
import LoadingSpinner from '../../components/Loader/LoadingSpinner';
import PageIntro from '../../components/PageIntro/PageIntro';

/* One partner. Admins can't edit a partner's details (partners change their
   own); they move the partner between statuses:

     pending    -> Verify     (dashboard and referral link open; partner is emailed)
     verified   -> Suspend    (needs a reason, seen only by the team)
     suspended  -> Reinstate  (back to verified)

   Each action confirms in a dialog first. */

const MIN_REASON = 5;

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
};

const capitalise = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const errorMessage = (err, fallback) => err?.data?.message || fallback;

const DetailItem = ({ label, value }) => (
  <div className='rounded-lg bg-surface px-3 py-2'>
    <p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>{label}</p>
    <p className='mt-0.5 break-words text-sm font-medium text-brand-navy'>{value || '—'}</p>
  </div>
);

const SectionTitle = ({ eyebrow, title }) => (
  <>
    <div className='text-[11px] font-semibold uppercase tracking-wider text-brand-orange'>{eyebrow}</div>
    <h2 className='mb-4 text-lg font-semibold text-brand-navy'>{title}</h2>
  </>
);

const CODE_STATE_STYLES = {
  current: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  past: 'border-border bg-surface text-foreground',
};

const SOURCE_LABELS = {
  partner_referral: 'Referral link',
  partner_import: 'Added in bulk',
};

export default function PartnerDetails() {
  const { id } = useParams();
  const { data: partner, isLoading } = useGetPartnerByIdQuery(id);
  const [verifyPartner, { isLoading: verifying }] = useVerifyPartnerMutation();
  const [suspendPartner, { isLoading: suspending }] = useSuspendPartnerMutation();
  const [reinstatePartner, { isLoading: reinstating }] = useReinstatePartnerMutation();

  const [approveName, { isLoading: approvingName }] = useApprovePartnerNameMutation();
  const [rejectName, { isLoading: rejectingName }] = useRejectPartnerNameMutation();

  // 'verify' | 'suspend' | 'reinstate' | 'approve-name' | 'reject-name' | null
  const [dialog, setDialog] = useState(null);
  /* The reason typed in the suspend or reject-name dialog. */
  const [reason, setReason] = useState('');

  const busy = verifying || suspending || reinstating || approvingName || rejectingName;

  const closeDialog = () => {
    if (busy) return;
    setDialog(null);
    setReason('');
  };

  const doVerify = async () => {
    try {
      await verifyPartner(partner.id).unwrap();
      toast.success(`${partner.name} is verified. They've been emailed.`);
      setDialog(null);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't verify this partner."));
    }
  };

  const doSuspend = async () => {
    if (reason.trim().length < MIN_REASON) {
      toast.error('Give a reason for the suspension.');
      return;
    }
    try {
      await suspendPartner({ id: partner.id, reason: reason.trim() }).unwrap();
      toast.success(`${partner.name} is suspended.`);
      setDialog(null);
      setReason('');
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't suspend this partner."));
    }
  };

  const doApproveName = async () => {
    try {
      await approveName(partner.id).unwrap();
      toast.success(`Name changed to "${partner.pending_name_request?.requested_name}".`);
      setDialog(null);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't approve the name change."));
    }
  };

  const doRejectName = async () => {
    if (reason.trim().length < MIN_REASON) {
      toast.error('Give the partner a reason.');
      return;
    }
    try {
      await rejectName({ id: partner.id, reason: reason.trim() }).unwrap();
      toast.success('Name change rejected. The partner sees your reason.');
      setDialog(null);
      setReason('');
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't reject the name change."));
    }
  };

  const doReinstate = async () => {
    try {
      await reinstatePartner(partner.id).unwrap();
      toast.success(`${partner.name} is reinstated.`);
      setDialog(null);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't reinstate this partner."));
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!partner) {
    return (
      <div className='mx-auto max-w-6xl'>
        <div className='flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card px-6 py-14 text-center'>
          <p className='text-lg font-semibold text-brand-navy'>Partner not found</p>
          <p className='mt-1 max-w-md text-sm text-muted-foreground'>This partner may have been removed.</p>
        </div>
      </div>
    );
  }

  const isOrganization = partner.account_type === 'organization';

  return (
    <div className='mx-auto max-w-6xl space-y-6'>
      <PageIntro title='Partner Details' description={partner.name} />

      {/* IDENTITY */}
      <section className='flex flex-col gap-4 rounded-2xl border bg-card p-5 shadow-ui-sm sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-4'>
          <div className='h-14 w-14 flex-shrink-0'>
            {partner.logo_url ? (
              <img src={partner.logo_url} alt={partner.name} className='h-full w-full rounded-full object-contain' />
            ) : (
              <div className='flex h-full w-full items-center justify-center rounded-full bg-brand-navy text-lg font-semibold text-white'>
                {getInitials(partner.name)}
              </div>
            )}
          </div>
          <div className='min-w-0'>
            <h2 className='truncate font-display text-lg font-bold text-brand-navy'>{partner.name}</h2>
            <p className='truncate text-sm text-muted-foreground'>{partner.contact?.email}</p>
            <div className='mt-2 flex flex-wrap items-center gap-3'>
              <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                {isOrganization ? 'Organization' : 'Individual'}
              </span>
              <Status label={capitalise(partner.status)} />
            </div>
          </div>
        </div>
      </section>

      {/* SUSPENSION NOTE (team only) */}
      {partner.status === 'suspended' && (
        <section className='rounded-2xl border border-red-200 bg-red-50 p-5'>
          <p className='text-[11px] font-semibold uppercase tracking-wider text-red-700'>Suspended</p>
          <p className='mt-1 text-sm text-red-900'>{partner.suspension_reason}</p>
          <p className='mt-2 text-xs text-red-800'>
            {formatDate(partner.suspended_at)}
            {partner.suspended_by ? ` by ${partner.suspended_by.name || partner.suspended_by.email}` : ''}. Only the team
            sees this reason; the partner is told to contact support.
          </p>
        </section>
      )}

      {/* NUMBER SUMMARY */}
      <section className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
        <StatTile label='Talents brought' value={partner.summary?.talents ?? 0} icon={<GraduationCap className='h-4 w-4' />} />
        <StatTile label='Clients brought' value={partner.summary?.clients ?? 0} icon={<Building2 className='h-4 w-4' />} />
        <StatTile label='Via referral link' value={partner.summary?.viaLink ?? 0} icon={<Link2 className='h-4 w-4' />} />
        <StatTile label='Added in bulk' value={partner.summary?.viaBulk ?? 0} icon={<Upload className='h-4 w-4' />} />
      </section>

      {/* CONTACT + VERIFICATION */}
      <section className='rounded-2xl border bg-card p-5 shadow-ui-sm'>
        <SectionTitle eyebrow='Registration' title='Contact & verification' />
        <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'>
          <DetailItem label={isOrganization ? 'Organization' : 'Partner'} value={partner.name} />
          <DetailItem label='Contact person' value={partner.contact?.name} />
          <DetailItem label='Email' value={partner.contact?.email} />
          <DetailItem label='Phone' value={partner.contact?.phone} />
          <DetailItem label='Registered' value={formatDate(partner.created_at)} />
          <DetailItem
            label='Verified'
            value={
              partner.verified_at
                ? `${formatDate(partner.verified_at)}${partner.verified_by ? ` by ${partner.verified_by.name || partner.verified_by.email}` : ''}`
                : 'Not yet'
            }
          />
        </div>
      </section>

      {/* REFERRAL CODES */}
      <section className='rounded-2xl border bg-card p-5 shadow-ui-sm'>
        <SectionTitle eyebrow='Referral' title='Referral codes' />
        {partner.codes?.length ? (
          <ul className='flex flex-wrap gap-2'>
            {partner.codes.map((code) => (
              <li
                key={code.code}
                className={`rounded-lg border px-3 py-1.5 font-mono text-sm ${CODE_STATE_STYLES[code.state] || CODE_STATE_STYLES.past}`}
                title={`Added ${formatDate(code.created_at)}`}
              >
                {code.code}
                <span className='ml-2 font-sans text-[10px] uppercase tracking-wider'>{code.state}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-sm text-muted-foreground'>No codes.</p>
        )}
        <p className='mt-3 text-xs text-muted-foreground'>
          Current and past codes sign people up to this partner while they're verified.
        </p>
      </section>

      {/* RECENT PEOPLE */}
      <section className='overflow-hidden rounded-2xl border bg-card shadow-ui-sm'>
        <div className='border-b px-5 py-4'>
          <div className='text-[11px] font-semibold uppercase tracking-wider text-brand-orange'>Referrals</div>
          <h2 className='text-lg font-semibold text-brand-navy'>Most recent sign-ups</h2>
        </div>
        {partner.recent_people?.length ? (
          <ul className='divide-y'>
            {partner.recent_people.map((person) => (
              <li key={person.id} className='flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm'>
                <div className='min-w-0'>
                  <p className='truncate font-medium text-brand-navy'>{person.name || person.email}</p>
                  <p className='truncate text-xs text-muted-foreground'>{person.email}</p>
                </div>
                <div className='flex items-center gap-3 text-xs text-muted-foreground'>
                  <span className='rounded-full bg-surface px-2 py-0.5 capitalize'>{person.kind}</span>
                  <span>{SOURCE_LABELS[person.registration_source] || person.registration_source}</span>
                  <span className='tabular-nums'>{formatDate(person.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className='px-5 py-6 text-sm text-muted-foreground'>Nobody has signed up through this partner yet.</p>
        )}
      </section>

      {/* NAME CHANGE: a verified partner asking for a new name, and past requests. */}
      {(partner.pending_name_request || partner.name_history?.length > 0) && (
        <section className='rounded-2xl border bg-card p-5 shadow-ui-sm'>
          <SectionTitle eyebrow='Profile' title='Name changes' />

          {partner.pending_name_request && (
            <div className='flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='min-w-0 text-sm text-amber-900'>
                <p className='text-[11px] font-semibold uppercase tracking-wider text-amber-700'>Waiting for approval</p>
                <p className='mt-1'>
                  <span className='text-amber-800'>{partner.pending_name_request.previous_name}</span>
                  {' → '}
                  <strong>{partner.pending_name_request.requested_name}</strong>
                </p>
                <p className='mt-1 text-xs text-amber-800'>Requested {formatDate(partner.pending_name_request.created_at)}</p>
              </div>
              <div className='flex shrink-0 gap-2'>
                <button
                  type='button'
                  onClick={() => setDialog('reject-name')}
                  className='rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50'
                >
                  Reject
                </button>
                <button
                  type='button'
                  onClick={() => setDialog('approve-name')}
                  className='rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90'
                >
                  Approve
                </button>
              </div>
            </div>
          )}

          {partner.name_history?.length > 0 && (
            <ul className='mt-4 divide-y rounded-xl border'>
              {partner.name_history.map((item) => (
                <li key={item.id} className='px-4 py-3 text-sm'>
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <p className='min-w-0'>
                      <span className='text-muted-foreground'>{item.previous_name}</span>
                      {' → '}
                      <span className='font-medium text-brand-navy'>{item.requested_name}</span>
                    </p>
                    <Status label={capitalise(item.status)} />
                  </div>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {formatDate(item.reviewed_at)}
                    {item.reviewed_by ? ` by ${item.reviewed_by}` : ''}
                    {item.rejection_reason ? `: ${item.rejection_reason}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* DECISION: at the bottom, so the admin has read everything above
          before acting. One action, depending on the status. */}
      <section className='flex flex-col gap-4 rounded-2xl border bg-card p-5 shadow-ui-sm sm:flex-row sm:items-center sm:justify-between'>
        <div className='min-w-0'>
          <div className='text-[11px] font-semibold uppercase tracking-wider text-brand-orange'>Decision</div>
          <p className='mt-1 text-sm text-muted-foreground'>
            {partner.status === 'pending' &&
              'Verify once the off-platform checks are done. Their dashboard and referral link open, and they are emailed.'}
            {partner.status === 'verified' &&
              'Suspend if this partner is misusing their account. Their dashboard and referral link pause until reinstated.'}
            {partner.status === 'suspended' && 'Reinstate to open their dashboard and referral link again.'}
          </p>
        </div>

        <div className='flex shrink-0 gap-2'>
          {partner.status === 'pending' && (
            <button
              type='button'
              onClick={() => setDialog('verify')}
              className='rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90'
            >
              Verify partner
            </button>
          )}
          {partner.status === 'verified' && (
            <button
              type='button'
              onClick={() => setDialog('suspend')}
              className='rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50'
            >
              Suspend partner
            </button>
          )}
          {partner.status === 'suspended' && (
            <button
              type='button'
              onClick={() => setDialog('reinstate')}
              className='rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90'
            >
              Reinstate partner
            </button>
          )}
        </div>
      </section>

      {/* CONFIRM: VERIFY */}
      <AlertDialog open={dialog === 'verify'} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verify "{partner.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Only verify once the off-platform checks are done. Their dashboard and referral link open straight away, and
              they're emailed to say they're approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doVerify();
              }}
              disabled={busy}
              className='bg-brand-navy text-white hover:bg-brand-navy/90'
            >
              {verifying ? 'Verifying…' : 'Verify partner'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRM: SUSPEND (with reason) */}
      <AlertDialog open={dialog === 'suspend'} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend "{partner.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Their dashboard locks and their referral link stops signing people up to them, until you reinstate them.
              They're told to contact support; the reason below is only seen by the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex flex-col gap-1.5'>
            <label htmlFor='suspend-reason' className='text-xs font-semibold text-muted-foreground'>
              Reason
            </label>
            <textarea
              id='suspend-reason'
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              placeholder='e.g. Referral link posted on spam sites'
              className='rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doSuspend();
              }}
              disabled={busy || reason.trim().length < MIN_REASON}
              className='bg-red-600 text-white hover:bg-red-700'
            >
              {suspending ? 'Suspending…' : 'Suspend partner'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRM: APPROVE NAME */}
      <AlertDialog open={dialog === 'approve-name'} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve the new name?</AlertDialogTitle>
            <AlertDialogDescription>
              "{partner.pending_name_request?.previous_name}" becomes "{partner.pending_name_request?.requested_name}"
              everywhere: their dashboard, this admin page and the "Registered via" column.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doApproveName();
              }}
              disabled={busy}
              className='bg-brand-navy text-white hover:bg-brand-navy/90'
            >
              {approvingName ? 'Approving…' : 'Approve name'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRM: REJECT NAME (reason shown to the partner) */}
      <AlertDialog open={dialog === 'reject-name'} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject the new name?</AlertDialogTitle>
            <AlertDialogDescription>
              They keep "{partner.pending_name_request?.previous_name}". The partner sees the reason below on their
              settings page, so write it for them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex flex-col gap-1.5'>
            <label htmlFor='reject-name-reason' className='text-xs font-semibold text-muted-foreground'>
              Reason (shown to the partner)
            </label>
            <textarea
              id='reject-name-reason'
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              placeholder="e.g. The new name doesn't match your registration documents"
              className='rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doRejectName();
              }}
              disabled={busy || reason.trim().length < MIN_REASON}
              className='bg-red-600 text-white hover:bg-red-700'
            >
              {rejectingName ? 'Rejecting…' : 'Reject name'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRM: REINSTATE */}
      <AlertDialog open={dialog === 'reinstate'} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reinstate "{partner.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Their dashboard opens again and their referral link starts signing people up to them again. The suspension
              reason is cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doReinstate();
              }}
              disabled={busy}
              className='bg-brand-navy text-white hover:bg-brand-navy/90'
            >
              {reinstating ? 'Reinstating…' : 'Reinstate partner'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
